import React from 'react';
import { MapContainer, TileLayer, Circle, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';

// Fix leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const RESTAURANT_COORDS = [46.00639, 8.95795];
const RESTAURANT_ADDRESS = 'Tasty Bites, Via Luigi Lavizzari 5, 6900 Lugano';

const DELIVERY_ZONES = [
  { radius: 4500, color: '#f59e0b', fillColor: '#f59e0b20', label: '2-4.5 km: CHF 6 (min CHF 59)' },
  { radius: 2000, color: '#22c55e', fillColor: '#22c55e25', label: '0-2 km: Free' },
];

// TEMPORARY: set to true to show delivery zone circles + legend again
const ZONES_ENABLED = false;

const openDirections = () => {
  const [lat, lng] = RESTAURANT_COORDS;
  const q = encodeURIComponent(RESTAURANT_ADDRESS);
  const isApple = /iPhone|iPad|iPod|Macintosh/i.test(navigator.userAgent);
  const url = isApple
    ? `https://maps.apple.com/?q=${q}&ll=${lat},${lng}`
    : `https://www.google.com/maps/search/?api=1&query=${q}`;
  window.open(url, '_blank', 'noopener');
};

const DeliveryZoneMap = ({ height = '400px', showLegend = true }) => {
  const { t } = useTranslation();

  return (
    <div className="relative" data-testid="delivery-zone-map">
      <MapContainer
        center={RESTAURANT_COORDS}
        zoom={13}
        style={{ height, width: '100%', borderRadius: '12px' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {ZONES_ENABLED && DELIVERY_ZONES.map((zone, i) => (
          <Circle
            key={i}
            center={RESTAURANT_COORDS}
            radius={zone.radius}
            pathOptions={{
              color: zone.color,
              fillColor: zone.fillColor,
              fillOpacity: 0.15,
              weight: 2,
              dashArray: i === 0 ? '8 4' : undefined,
            }}
          />
        ))}
        <Marker position={RESTAURANT_COORDS} eventHandlers={{ click: openDirections }}>
          <Popup>
            <strong>Tasty Bites</strong><br />
            Via Luigi Lavizzari 5<br />
            6900 Lugano<br />
            <button
              onClick={openDirections}
              style={{ marginTop: '6px', color: '#d97706', fontWeight: 600, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
              data-testid="map-directions-link"
            >
              {t('contact.getDirections', 'Get Directions')}
            </button>
          </Popup>
        </Marker>
      </MapContainer>

      {showLegend && ZONES_ENABLED && (
        <div className="mt-4 flex flex-wrap gap-3" data-testid="delivery-zone-legend">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-full">
            <span className="w-3 h-3 rounded-full bg-green-500"></span>
            <span className="text-sm text-green-400 font-medium">0-2 km: {t('delivery.free', 'Free')} (min. CHF 30)</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full">
            <span className="w-3 h-3 rounded-full bg-amber-500"></span>
            <span className="text-sm text-amber-400 font-medium">2-4.5 km: CHF 8 (min. CHF 69)</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-[#1C1C22] border border-zinc-700 rounded-full">
            <span className="w-3 h-3 rounded-full bg-zinc-500"></span>
            <span className="text-sm text-zinc-400 font-medium">4.5+ km: {t('delivery.pickupOnly', 'Pickup only')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryZoneMap;
